<template>
  <form-section :title="title">
    <template slot="controls">
      <content-actions
        v-model="value"
        :channel="$route.query.channel"
        apply-template-enabled
        copy-enabled
        :model="model"
      />
      <slot name="selected-template-info"></slot>
    </template>
    <el-row>
      <el-col :lg="10" :xl="8">
        <form-item :label="$t('Campaign_SmsForm_From_From')" prop="from">
          <el-autocomplete
            v-model="value.sms.from"
            :fetch-suggestions="querySearchFromAddr"
            data-qa="sms-from-name-input"
          />
        </form-item>
        <form-item
          :label="$t('Campaign_SmsForm_From_Text')"
          prop="text"
          :description="messageCounterMsg"
        >
          <snippet-input
            type="textarea"
            v-model="value.sms.text"
            :optimove="isOptimove"
            :salesforce="isSalesforce"
            data-qa="sms-message-input"
          />
        </form-item>

        <!-- Voucher code settings -->
        <template v-if="model.voucher_codes_enabled">
          <form-item
            prop="voucher_code_enabled"
            :label="$t('CampaignForm_VoucherType_Title')"
          >
            <enum-select
              v-model="value.voucher_code_enabled"
              :options="voucherTypesLabels"
              :number="true"
              data-qa="voucher_code_enabled"
            />
          </form-item>

          <form-item
            prop="voucher_code_position"
            :label="$t('CampaignForm_VoucherPosition_Title')"
            v-if="value.voucher_code_enabled === 1"
          >
            <enum-select
              v-model="value.voucher_code_position"
              :options="voucherPositionsLabels"
              :number="true"
              data-qa="voucher_code_position"
            />
          </form-item>
        </template>

        <div v-if="optOutSettings.enabled">
          <form-item prop="sms_unsubscribe">
            <toggle
              v-if="optOutSettings.type === smsOptOutTypeEnum.LINK"
              v-model="value.sms_unsubscribe"
              :text="$t('Campaign_SmsForm_Unsubscribe_Link')"
            />
          </form-item>
          <div v-if="optOutSettings.type === smsOptOutTypeEnum.REPLY">
            <form-item
              v-if="value.sms_unsubscribe_with_reply"
              :label="$t('CampaignForm_Sms_unsubscribe_message')"
            >
              <el-input
                :readonly="true"
                :disabled="true"
                :value="optOutMessage"
              />
            </form-item>
            <form-item prop="sms_unsubscribe_with_reply">
              <toggle
                v-model="value.sms_unsubscribe_with_reply"
                :text="$t('CampaignForm_Sms_unsubscribe_with_reply')"
              />
            </form-item>
            <form-item
              v-if="!multilang && value.sms_unsubscribe_with_reply"
              prop="sms_unsubscribe_language"
              :label="$t('CampaignForm_Sms_unsubscribe_language')"
            >
              <el-select v-model="value.sms_unsubscribe_language">
                <el-option :key="0" value="" label="-" />
                <el-option
                  v-for="(language, index) in Object.keys(
                    optOutSettings.messages
                  )"
                  :key="index + 1"
                  :value="language"
                  :label="optOutSettings.messages[language].langName"
                />
              </el-select>
            </form-item>
          </div>
        </div>
      </el-col>
      <el-col :lg="14" :xl="16" class="preview">
        <push-preview-section
          platform="ios"
          :text="previewMessageText"
          :title="value.sms.from"
          :app-name="appName"
          :app-icon="appIcon"
          :new-frame="true"
        >
        </push-preview-section>
      </el-col>
    </el-row>
  </form-section>
</template>

<script>
import FormSection from '@/common/components/form/FormSection'
import PushPreviewSection from '@/common/components/push/PushPreviewSection'
import { EventBus } from '@/core/EventBus'
import MessagesIosIcon from '@/img/icon/messages-ios.svg'
import { channelTypes } from '@/modules/campaign-manage/enum/Labels'
import FormItem from '@/common/components/form/FormItem'
import SnippetInput from '@/modules/campaign-manage/components/SnippetInput'
import { smsOptOutTypeEnum } from '@/modules/settings/enum/SmsOptOutTypeEnum'
import Toggle from '@/common/components/form/Toggle'
import { campaignTriggerTypes } from '@/modules/campaign-manage/enum/CampaignTriggerTypesEnum'
import SmsCounterHelper from '@/common/helpers/SmsCounterHelper'
import axios from 'axios'
import ContentActions from '@/modules/campaign-manage/components/ContentActions'
import { voucherTypesLabels } from '@/modules/campaign-manage/enum/VoucherTypesEnum'
import {
  voucherPositionsLabels,
  voucherPositions
} from '@/modules/campaign-manage/enum/VoucherPositionsEnum'
import EnumSelect from '@/common/components/form/EnumSelect'

export default {
  name: 'SMS',
  components: {
    ContentActions,
    Toggle,
    SnippetInput,
    FormItem,
    FormSection,
    PushPreviewSection,
    EnumSelect
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    model: {
      type: Object,
      required: true
    },
    title: {
      required: true
    },
    multilang: {
      type: Boolean,
      default: false
    },
    lang: {
      type: String,
      required: false
    },
    optOutSettings: {
      type: Object,
      required: false
    },
    shortenUrl: {
      type: Boolean,
      default: false
    },
    shortenUrlDomain: {
      type: Boolean,
      default: null
    }
  },
  data() {
    return {
      smsOptOutTypeEnum,
      voucherTypesLabels,
      voucherPositions,
      voucherPositionsLabels
    }
  },
  created() {
    this.smsHelper = new SmsCounterHelper()
  },
  computed: {
    isOptimove() {
      return this.model.trigger === campaignTriggerTypes.TRIGGER_OPTIMOVE
    },
    isSalesforce() {
      return this.model.trigger === campaignTriggerTypes.TRIGGER_SALESFORCE
    },
    appIcon: function() {
      return MessagesIosIcon
    },
    appName: function() {
      return 'Messages'
    },
    previewMessageText() {
      let text = this.value.sms.text
        ? this.value.sms.text
        : this.$t('Campaign_SmsPreview_Text_Placeholder')

      if (this.shortenUrl) {
        text = this.replaceLinksToShort(text)
      }

      let unsubscribeText = ''

      if (this.value.sms_unsubscribe) {
        unsubscribeText = ` \r\n${this.previewUrl}`
      } else if (this.value.sms_unsubscribe_with_reply) {
        unsubscribeText = ` \r\n${this.optOutMessage}`
      }

      text = this.addVoucherCode(text)

      return `${text}${unsubscribeText}`
    },
    optOutMessage() {
      const lang = this.multilang
        ? this.lang
        : this.value.sms_unsubscribe_language

      if (
        !this.value.sms_unsubscribe_with_reply ||
        !this.optOutSettings.messages ||
        !this.optOutSettings.messages.hasOwnProperty(lang)
      ) {
        return ''
      }

      return this.optOutSettings.messages[lang].message
    },
    messageCounter() {
      let text = this.value.sms.text

      if (this.shortenUrl) {
        text = this.replaceLinksToShort(text)
      }

      // Replace {{ tokens }} with empty strings.
      // Since we do not know what the token will be converted to, it is probably best to replace them with empty strings.
      text = text.replaceAll(/{{(.*?)}}/g, '')

      if (this.value.sms_unsubscribe) {
        text += ` \r\n${this.previewUrl}`
      } else if (this.value.sms_unsubscribe_with_reply) {
        text += ` \r\n${this.optOutMessage}`
      }

      text = this.addVoucherCode(text)

      return this.smsHelper.count(text)
    },
    messageCounterMsg() {
      let message =
        this.messageCounter.length +
        this.$t('Campaign_SmsForm_MessagePartOf') +
        this.messageCounter.perMessages +
        ' (' +
        this.messageCounter.messages +
        this.$t('Campaign_SmsForm_MessagePart')

      // add 'estimated' to the count message if a token is used in a message
      if (
        this.value.sms_unsubscribe ||
        this.value.sms_unsubscribe_with_reply ||
        this.value.sms.text.match(/{{(.*?)}}/) ||
        this.value.voucher_code_enabled
      ) {
        message += this.$t('Campaign_SmsForm_MessagePartEstimated')
      } else {
        message += this.$t('Campaign_SmsForm_MessagePartActual')
      }

      return message
    },
    previewUrl() {
      if (this.shortenUrlDomain) {
        return this.shortenUrlDomain + '/5ZynP2x'
      } else {
        return this.$store.state.app.domains.short + '/5ZynP2x'
      }
    }
  },
  methods: {
    copyFrom() {
      EventBus.$emit('copyFromDialog', channelTypes.sms)
    },
    querySearchFromAddr(queryString, cb) {
      axios
        .get('/api/sms/sms-from-name/autocomplete', {
          params: {
            project_id: this.$route.query.project_id,
            term: queryString
          }
        })
        .then(({ data }) => {
          cb(data)
        })
    },
    replaceLinksToShort(messageText) {
      const urlRegex = /(https?:\/\/[^\s]+)/g
      const attributeBracketsRegex = /\{{.*?\}}/g

      // remove spaces inside attribute brackets
      messageText = messageText.replace(attributeBracketsRegex, function(
        match
      ) {
        return match.replaceAll(' ', '')
      })
      messageText = messageText.replace(urlRegex, this.previewUrl)
      // return non url part attribute spaces
      return messageText.replace(attributeBracketsRegex, function(match) {
        match = match.replace('{{', '{{ ')
        return match.replace('}}', ' }}')
      })
    },
    addVoucherCode(text) {
      if (this.model.voucher_codes_enabled && this.value.voucher_code_enabled) {
        if (this.value.voucher_code_position === voucherPositions.TOP) {
          text = this.$t('CampaignForm_VoucherCode_Sample') + '\n' + text
        } else {
          text = text + '\n' + this.$t('CampaignForm_VoucherCode_Sample')
        }
      }

      return text
    }
  },
  watch: {
    'value.sms.text': {
      handler(text) {
        if (this.messageCounter.messages > this.smsHelper.maxMessages) {
          let max = this.messageCounter.perMessage * this.smsHelper.maxMessages
          setTimeout(() => {
            this.$set(this.value.sms, 'text', text.substring(0, max))
          }, 100)
        }
      }
    }
  }
}
</script>

<style scoped>
.preview {
  background-color: #f8fafc;
  padding-top: 30px;
  padding-bottom: 30px;
}

>>> .toggle-container {
  padding-left: 0px !important;
  padding-top: 5px !important;
}

>>> .description {
  margin-top: -20px;
  text-align: right;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/content/channels/SMS.vue